// Eskom PARCC — Settings (self-service workspace preferences).
//
// Reachable from BOTH themes (the Lumen header's gear, the Legacy header's
// Settings button) — this is where a person switches between Legacy and Lumen, so
// it must never be reachable from only one of them.
//
// Four sections, all backed by real storage:
//   • Appearance          — theme, colour mode, sidebar default, density
//                           (lib/uiPrefs → localStorage + the User row).
//   • My notifications    — the four EVENT classes, on the caller's AccessGrant
//                           via the self-scoped account endpoint.
//   • Regional & format   — time zone + date/number locale (lib/formats reads it).
//   • Session & security  — change password; sign out of every session.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, LogOut, Monitor, Sun, Moon, Sparkles } from 'lucide-react';
import { useAiAvailability } from '../../lib/aiMode';
import { Banner, Button, Card, Select, Switch, useToast } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { adminApi, type NotificationEventPrefs, type DigestCadence } from '../../lib/adminApi';
import { isReadOnly } from '../../lib/readOnly';
import {
  NUMBER_FORMAT_OPTIONS,
  TIME_ZONE_OPTIONS,
  useUiPrefs,
  type Density,
  type SidebarDefault,
  type UiVariant,
} from '../../lib/uiPrefs';
import { ChangePassword } from '../auth/ChangePassword';

/** A labelled row of mutually-exclusive buttons. Renders identically in both
 *  themes (Lumen restyles `.lm-segmented`); every option is a real button, so
 *  the parity check sees the same control set either way. */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (next: T) => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <div>
      <div
        className="smallcaps"
        style={{ marginBottom: 8, fontWeight: 700, color: 'var(--muted)' }}
      >
        {label}
      </div>
      <div className="lm-segmented" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={o.value === value ? 'is-active' : undefined}
            aria-pressed={o.value === value}
            disabled={disabled}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One notification switch with its explanation. */
function PrefRow({
  title,
  hint,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 0',
        borderTop: '1px solid var(--line)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>{title}</div>
        <div className="small">{hint}</div>
      </div>
      <Switch checked={checked} onChange={onChange} ariaLabel={title} disabled={disabled} />
    </div>
  );
}

const THEME_CARDS: readonly {
  value: UiVariant;
  name: string;
  blurb: string;
  swatches: readonly string[];
  isNew?: boolean;
}[] = [
  {
    value: 'legacy',
    name: 'Legacy',
    blurb: 'The current production interface — dark-first and dense. Unchanged.',
    swatches: ['linear-gradient(135deg,#0a2740,#04131f)', '#0b2135'],
  },
  {
    value: 'lumen',
    name: 'Lumen',
    blurb: 'The redesign — calm, light-first, grouped navigation and clearer hierarchy.',
    swatches: ['#ffffff', 'linear-gradient(135deg,#00a3e0,#0072ce)'],
    isNew: true,
  },
];

export function SettingsPage(): React.JSX.Element {
  const prefs = useUiPrefs();
  // Eligibility (the admin's grant) + opt-in (this switch). The switch is shown
  // to everyone but only operable once granted, so a phased rollout is visible
  // to the people waiting for it rather than silently absent.
  const ai = useAiAvailability();
  const { user, role, signOutUser } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const readOnly = isReadOnly();

  // ---- My notifications -------------------------------------------------
  const prefsQuery = useQuery({
    queryKey: ['myNotificationPrefs'],
    queryFn: () => adminApi.getMyNotificationPrefs(),
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const [notify, setNotify] = useState<NotificationEventPrefs | null>(null);
  useEffect(() => {
    if (prefsQuery.data) setNotify(prefsQuery.data);
  }, [prefsQuery.data]);

  const saveNotify = useMutation({
    mutationFn: (next: NotificationEventPrefs) => adminApi.setMyNotificationPrefs(next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['myNotificationPrefs'] });
      toast.show('Notification settings saved.', 'success');
    },
    onError: (err: unknown) =>
      toast.show(
        `Could not save your notification settings: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      ),
  });

  function patchNotify(patch: Partial<NotificationEventPrefs>): void {
    if (!notify) return;
    const next = { ...notify, ...patch };
    setNotify(next); // optimistic — the switch answers immediately
    saveNotify.mutate(next);
  }

  // The email-digest cadence has its OWN endpoint (kept separate from the four
  // event switches so neither write clobbers the other). Owner feedback Aug 2026.
  const saveCadence = useMutation({
    mutationFn: (cadence: DigestCadence) => adminApi.setDigestCadence(cadence),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['myNotificationPrefs'] });
      toast.show('Digest setting saved.', 'success');
    },
    onError: (err: unknown) =>
      toast.show(
        `Could not save your digest setting: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      ),
  });

  function setCadence(cadence: DigestCadence): void {
    if (!notify) return;
    setNotify({ ...notify, digestCadence: cadence }); // optimistic
    saveCadence.mutate(cadence);
  }

  // ---- Session & security ------------------------------------------------
  async function revokeSessions(): Promise<void> {
    setRevoking(true);
    try {
      await adminApi.revokeMySessions();
      toast.show('Signed out of all sessions. Signing you out here too…', 'success');
      // Every other device's token is now unrenewable; close this one as well so
      // "all sessions" means what it says.
      await signOutUser();
    } catch (err) {
      toast.show(
        `Could not sign out of all sessions: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    } finally {
      setRevoking(false);
    }
  }

  return (
    <>
      <Card
        title="Appearance"
        subtitle="Interface theme, colour mode, density and layout. Saved to your account, so it follows you across devices."
      >
        <div
          className="smallcaps"
          style={{ marginBottom: 10, fontWeight: 700, color: 'var(--muted)' }}
        >
          Interface theme
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          {THEME_CARDS.map((card) => {
            const active = prefs.variant === card.value;
            return (
              <button
                key={card.value}
                type="button"
                className="card"
                aria-pressed={active}
                aria-label={`${card.name} theme`}
                onClick={() => prefs.setPrefs({ variant: card.value })}
                style={{
                  position: 'relative',
                  flex: '1 1 220px',
                  minWidth: 220,
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: 15,
                  borderColor: active ? 'var(--accent)' : undefined,
                  boxShadow: active ? '0 0 0 1px var(--accent) inset' : undefined,
                }}
              >
                <div style={{ display: 'flex', gap: 5, marginBottom: 11 }}>
                  {card.swatches.map((s, i) => (
                    <span
                      key={i}
                      aria-hidden
                      style={{
                        width: 38,
                        height: 26,
                        borderRadius: 5,
                        background: s,
                        border: '1px solid var(--line)',
                        display: 'inline-block',
                      }}
                    />
                  ))}
                </div>
                <div style={{ fontWeight: 700, fontSize: 'var(--fs-md)' }}>
                  {card.name}
                  {card.isNew && (
                    <span
                      style={{
                        fontSize: 10,
                        color: 'var(--accent)',
                        background: 'var(--accent-soft)',
                        padding: '2px 7px',
                        borderRadius: 999,
                        marginLeft: 6,
                        verticalAlign: 'middle',
                      }}
                    >
                      New
                    </span>
                  )}
                </div>
                <div className="small" style={{ marginTop: 2, lineHeight: 1.4 }}>
                  {card.blurb}
                </div>
              </button>
            );
          })}
        </div>

        {prefs.variant === 'legacy' && (
          <Banner tone="warn">
            Legacy is the classic interface. Lumen is the redesign — calm, light-first, with grouped
            navigation, a collapsible sidebar and a command palette. Every screen, control and
            export works the same in both; you can switch back here at any time.
          </Banner>
        )}

        <div
          style={{
            display: 'flex',
            gap: 26,
            flexWrap: 'wrap',
            marginTop: 18,
            paddingTop: 16,
            borderTop: '1px solid var(--line)',
          }}
        >
          <Segmented<'light' | 'dark'>
            label="Colour mode"
            value={prefs.theme}
            onChange={(v) => prefs.setPrefs({ theme: v })}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
          <Segmented<SidebarDefault>
            label="Sidebar default"
            value={prefs.sidebarDefault}
            onChange={(v) => prefs.setPrefs({ sidebarDefault: v })}
            options={[
              { value: 'expanded', label: 'Expanded' },
              { value: 'collapsed', label: 'Collapsed' },
            ]}
          />
          <Segmented<Density>
            label="Density"
            value={prefs.density}
            onChange={(v) => prefs.setPrefs({ density: v })}
            options={[
              { value: 'comfort', label: 'Comfort' },
              { value: 'balanced', label: 'Balanced' },
              { value: 'compact', label: 'Compact' },
            ]}
          />
        </div>
        {prefs.variant === 'legacy' && (
          <div className="small" style={{ marginTop: 12 }}>
            <Monitor size={13} aria-hidden style={{ verticalAlign: '-2px', marginRight: 5 }} />
            Sidebar and density apply to Lumen; Legacy keeps its own fixed layout. Colour mode
            applies to both.
          </div>
        )}
      </Card>

      <div style={{ height: 16 }} />

      <Card
        title="AI Compliance Engine"
        subtitle="Reads a transaction's supporting documents and proposes a compliance verdict for each procedure — with a cited reason and a confidence level. It never decides: you confirm or override every proposal before it counts."
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '12px 0',
            borderTop: '1px solid var(--line)',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 'var(--fs-md)' }}>
              <Sparkles size={14} aria-hidden style={{ verticalAlign: '-2px', marginRight: 6 }} />
              AI Mode
            </div>
            <div className="small">
              {ai.eligible
                ? 'Adds the AI workspace and the “Run AI review” panel on working papers. Turning it off hides them again — nothing else about the app changes.'
                : 'Ask an administrator to enable the AI Compliance Engine for your account.'}
            </div>
          </div>
          <Switch
            checked={ai.optedIn}
            onChange={(v) => prefs.setPrefs({ aiMode: v ? 'on' : 'off' })}
            ariaLabel="AI Mode"
            disabled={readOnly || !ai.eligible}
          />
        </div>
        {ai.available && (
          <div className="small" style={{ marginTop: 12 }}>
            Evidence stays resident in South Africa (africa-south1). Every verdict cites the
            document and page it relied on, and needs your confirmation before it is recorded.
          </div>
        )}
      </Card>

      <div style={{ height: 16 }} />

      <Card
        title="My notifications"
        subtitle="Which alerts reach you. Account and security notices are always delivered, and an administrator controls whether you receive alerts in-app, by email or both."
      >
        {prefsQuery.isError && (
          <Banner tone="bad" title="Could not load your notification settings.">
            <span className="small">
              {prefsQuery.error instanceof Error ? prefsQuery.error.message : 'Please try again.'}
            </span>
          </Banner>
        )}
        {notify && (
          <div>
            <PrefRow
              title="Allocations"
              hint="When transactions are assigned to you"
              checked={notify.notifyAllocations}
              onChange={(v) => patchNotify({ notifyAllocations: v })}
              disabled={readOnly || saveNotify.isPending}
            />
            <PrefRow
              title="Revision requests"
              hint="When a manager sends a working paper back"
              checked={notify.notifyRevisions}
              onChange={(v) => patchNotify({ notifyRevisions: v })}
              disabled={readOnly || saveNotify.isPending}
            />
            <PrefRow
              title="QA notes"
              hint="Replies and resolutions on your papers"
              checked={notify.notifyQaNotes}
              onChange={(v) => patchNotify({ notifyQaNotes: v })}
              disabled={readOnly || saveNotify.isPending}
            />
            <PrefRow
              title="Completed reviews"
              hint="Digest when reviews in your division complete"
              checked={notify.notifyReviewDigest}
              onChange={(v) => patchNotify({ notifyReviewDigest: v })}
              disabled={readOnly || saveNotify.isPending}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                paddingTop: 12,
                marginTop: 8,
                borderTop: '1px solid var(--border)',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>Email digest</div>
                <div className="small">
                  A batched summary by email instead of one message per transaction. The in-app bell
                  is unchanged.
                </div>
              </div>
              <Select
                ariaLabel="Email digest cadence"
                value={notify.digestCadence}
                onChange={(v) => setCadence(v as DigestCadence)}
                disabled={readOnly || saveCadence.isPending}
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'daily', label: 'Daily' },
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'both', label: 'Daily + weekly' },
                ]}
              />
            </div>
          </div>
        )}
        {!notify && !prefsQuery.isError && <div className="small">Loading your settings…</div>}
      </Card>

      <div style={{ height: 16 }} />

      <Card
        title="Regional & format"
        subtitle="How dates and numbers are rendered for you. The Rand format is fixed by the reporting standard and is not affected."
      >
        <div
          className="filters"
          style={{ marginBottom: 0, border: 0, padding: 0, background: 'none', boxShadow: 'none' }}
        >
          <label>
            Time zone
            <Select
              ariaLabel="Time zone"
              value={prefs.timeZone}
              onChange={(v) => prefs.setPrefs({ timeZone: v })}
              options={TIME_ZONE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </label>
          <label>
            Date & number format
            <Select
              ariaLabel="Date & number format"
              value={prefs.numberFormat}
              onChange={(v) => prefs.setPrefs({ numberFormat: v })}
              options={NUMBER_FORMAT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </label>
        </div>
      </Card>

      <div style={{ height: 16 }} />

      <Card title="Session & security" subtitle="Your sign-in credentials and active sessions.">
        <div className="actions">
          <Button onClick={() => setPasswordOpen(true)}>
            <KeyRound size={15} aria-hidden /> Change password
          </Button>
          <Button
            variant="danger"
            disabled={revoking || readOnly}
            onClick={() => void revokeSessions()}
            title="Revokes every signed-in session for your account, including this one."
          >
            <LogOut size={15} aria-hidden />{' '}
            {revoking ? 'Signing out…' : 'Sign out of all sessions'}
          </Button>
        </div>
        <div className="small" style={{ marginTop: 12 }}>
          {prefs.theme === 'dark' ? (
            <Moon size={13} aria-hidden style={{ verticalAlign: '-2px', marginRight: 5 }} />
          ) : (
            <Sun size={13} aria-hidden style={{ verticalAlign: '-2px', marginRight: 5 }} />
          )}
          Signed in as {user?.email ?? '—'}
          {role ? ` · ${role}` : ''}. Access is provisioned by your administrator — contact them to
          change your role or division.
        </div>
      </Card>

      <ChangePassword open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </>
  );
}
